import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Home, Phone, Search, Music, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import FloatingShapes from "@/components/effects/FloatingShapes";
import AnimatedSection from "@/components/ui/animated-section";
import { GradientText } from "@/components/ui/gradient-text";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  const suggestedLinks = [
    { label: "Home", path: "/", icon: Home },
    { label: "Artists", path: "/artists", icon: Music },
    { label: "Services", path: "/services", icon: Search },
    { label: "Contact Us", path: "/contact", icon: Phone },
  ];

  return (
    <div className="min-h-screen bg-primary relative overflow-hidden flex items-center justify-center px-4">
      <FloatingShapes variant="hero" />
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/80" />
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(hsl(var(--primary-foreground)) 1px, transparent 1px),
                           linear-gradient(90deg, hsl(var(--primary-foreground)) 1px, transparent 1px)`,
          backgroundSize: "50px 50px",
        }}
      />
      <div className="absolute top-1/4 left-10 w-64 h-64 bg-accent/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-1/4 right-10 w-80 h-80 bg-accent/15 rounded-full blur-3xl" />

      <div className="max-w-lg w-full text-center relative z-10">
        <AnimatedSection animation="fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/20 backdrop-blur-sm rounded-full mb-8 border border-accent/30">
            <Sparkles className="h-4 w-4 text-accent" />
            <span className="text-sm font-semibold text-accent">Page Not Found</span>
          </div>

          <div className="relative mb-8">
            <h1 className="text-[150px] md:text-[200px] font-black text-primary-foreground/5 leading-none select-none">
              404
            </h1>
            <div className="absolute inset-0 flex items-center justify-center">
              <GradientText variant="accent" className="text-6xl md:text-8xl font-bold drop-shadow-[0_0_40px_hsl(var(--accent)/0.5)]">
                Oops!
              </GradientText>
            </div>
          </div>

          <h2 className="text-2xl md:text-3xl font-bold text-primary-foreground mb-4">
            This Page Wandered Off
          </h2>
          <p className="text-primary-foreground/70 mb-8 max-w-md mx-auto">
            The page you're looking for doesn't exist or has been moved. Let's get you back on track.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button size="lg" variant="glow" className="group min-w-[180px]" asChild>
              <Link to="/">
                <Home className="mr-2 h-4 w-4" />
                Back to Home
              </Link>
            </Button>
            <Button
              size="lg"
              variant="glass"
              className="border-primary-foreground/30 text-primary-foreground min-w-[180px]"
              asChild
            >
              <Link to="/contact">
                <Phone className="mr-2 h-4 w-4" />
                Contact Support
              </Link>
            </Button>
          </div>

          <div className="border-t border-primary-foreground/10 pt-8">
            <p className="text-sm text-primary-foreground/50 mb-4 uppercase tracking-wider font-medium">
              Popular pages you might be looking for
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {suggestedLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-foreground/5 backdrop-blur-sm border border-primary-foreground/10 text-primary-foreground text-sm hover:bg-primary-foreground/10 hover:border-accent/30 hover:text-accent transition-all duration-300"
                >
                  <link.icon className="h-4 w-4 group-hover:scale-110 transition-transform" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </AnimatedSection>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background/50 to-transparent" />
    </div>
  );
};

export default NotFound;
